import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMap } from "react-leaflet";
import LeafletFloorMap from "../../../components/LeafletFloorMap";
import { parseNames } from "../../../utils/nameParser";
import { ReactComponent as ClickIcon } from "../../../assets/icons/navigation/click.svg";
import { ReactComponent as HandIcon } from "../../../assets/icons/navigation/hand.svg";
import { ReactComponent as EyeIcon } from "../../../assets/icons/navigation/eye.svg";
import { ReactComponent as LineIcon } from "../../../assets/icons/navigation/line.svg";
import { ReactComponent as VectorIcon } from "../../../assets/icons/navigation/vector.svg";
import { ReactComponent as WalkIcon } from "../../../assets/icons/navigation/walk.svg";
import { ReactComponent as EraseIcon } from "../../../assets/icons/navigation/erase.svg";
import {
  NavigationAPI,
  NavigationGraphAPI,
  ShapeAPI,
  DestinationAPI,
  FloorAPI,
  resolveAsset,
} from "../../../services/api";

function getFloorLabel(f, t) {
  const name = f.name || "";
  const level = f.level;
  if (!name) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  const lowerName = name.toLowerCase().trim();
  const isGeneric =
    lowerName === `floor${level}` ||
    lowerName === `floor ${level}` ||
    lowerName === `floor_${level}` ||
    lowerName === `ชั้น${level}` ||
    lowerName === `ชั้น ${level}` ||
    lowerName === `ชั้นที่${level}` ||
    lowerName === `ชั้นที่ ${level}`;
  if (isGeneric) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  return name;
}

function makeCorridor(points, index, t) {
  return {
    id: `local-${Date.now()}-${index}`,
    name: t ? t("admin.navigation.corridorName", { index: index + 1 }) : `ทางเดิน ${index + 1}`,
    points: points.map((p) => ({
      x: Math.round(p.x),
      y: Math.round(p.y),
    })),
    bidirectional: true,
  };
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

const CONNECT_THRESHOLD = 44;

// Drawing and preview tools are integrated into the dropdown

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

function projectPointToSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (!lenSq) return { point: a, t: 0, distance: distance(point, a) };

  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq));
  const projected = { x: a.x + dx * t, y: a.y + dy * t };
  return { point: projected, t, distance: distance(point, projected) };
}

function nearestPointOnCorridors(corridors, point) {
  let best = null;

  corridors.forEach((corridor, corridorIndex) => {
    const points = Array.isArray(corridor.points) ? corridor.points : [];
    for (let pointIndex = 0; pointIndex < points.length - 1; pointIndex += 1) {
      const projected = projectPointToSegment(point, points[pointIndex], points[pointIndex + 1]);
      if (!best || projected.distance < best.distance) {
        best = {
          ...projected,
          corridorIndex,
          pointIndex,
        };
      }
    }
  });

  return best;
}

function snapStrokeToCorridors(corridors, stroke) {
  if (stroke.length < 2) return { points: [], mode: "ignored" };

  const firstPoint = stroke[0];
  const lastPoint = stroke[stroke.length - 1];
  const firstSnap = nearestPointOnCorridors(corridors, firstPoint);
  const lastSnap = nearestPointOnCorridors(corridors, lastPoint);
  const firstConnected = firstSnap && firstSnap.distance <= CONNECT_THRESHOLD;
  const lastConnected = lastSnap && lastSnap.distance <= CONNECT_THRESHOLD;

  if (!firstConnected && !lastConnected) {
    return { points: stroke, mode: "new" };
  }

  const points = [
    firstConnected ? firstSnap.point : firstPoint,
    lastConnected ? lastSnap.point : lastPoint,
  ].map((point) => ({
    x: Math.round(point.x),
    y: Math.round(point.y),
  }));

  if (distance(points[0], points[1]) < 2) {
    return { points: [], mode: "ignored" };
  }

  return { points, mode: "connected" };
}

function straightStroke(points) {
  const deduped = points.reduce((acc, point) => {
    const prev = acc[acc.length - 1];
    if (!prev || distance(prev, point) >= 6) {
      acc.push(point);
    }
    return acc;
  }, []);

  if (deduped.length <= 2) {
    return deduped.map((point) => ({
      x: Math.round(point.x),
      y: Math.round(point.y),
    }));
  }

  return [deduped[0], deduped[deduped.length - 1]].map((point) => ({
    x: Math.round(point.x),
    y: Math.round(point.y),
  }));
}

function normalizeFreehandPath(points) {
  return straightStroke(points);
}

function mergeStraightStrokeIntoDraft(draft, stroke) {
  if (!draft.length) return { points: stroke, mode: "new" };
  if (stroke.length < 2) return { points: draft, mode: "ignored" };

  const firstDraftPoint = draft[0];
  const lastDraftPoint = draft[draft.length - 1];
  const firstStrokePoint = stroke[0];
  const lastStrokePoint = stroke[stroke.length - 1];

  if (distance(lastDraftPoint, firstStrokePoint) <= CONNECT_THRESHOLD) {
    return { points: [...draft, lastStrokePoint], mode: "append" };
  }

  if (distance(lastDraftPoint, lastStrokePoint) <= CONNECT_THRESHOLD) {
    return { points: [...draft, firstStrokePoint], mode: "append" };
  }

  if (distance(firstDraftPoint, lastStrokePoint) <= CONNECT_THRESHOLD) {
    return { points: [firstStrokePoint, ...draft], mode: "prepend" };
  }

  if (distance(firstDraftPoint, firstStrokePoint) <= CONNECT_THRESHOLD) {
    return { points: [lastStrokePoint, ...draft], mode: "prepend" };
  }

  return { points: draft, mode: "ignored" };
}

function buildCorridorPayload(corridors, t) {
  return corridors.map((corridor, index) => ({
    id: typeof corridor.id === "number" ? corridor.id : undefined,
    name: corridor.name || (t ? t("admin.navigation.corridorName", { index: index + 1 }) : `ทางเดิน ${index + 1}`),
    points: corridor.points,
    bidirectional: corridor.bidirectional !== false,
  }));
}

function normalizeCorridors(data, t) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items ||
    data?.corridors ||
    data?.data?.items ||
    data?.data?.corridors ||
    [];

  if (!Array.isArray(raw)) return [];

  return raw
    .map((corridor, index) => {
      const points =
        corridor.points ||
        corridor.path ||
        corridor.polyline ||
        corridor.coordinates ||
        [];

      return {
        id: corridor.id ?? `server-${index}`,
        name: corridor.name || (t ? t("admin.navigation.corridorName", { index: index + 1 }) : `ทางเดิน ${index + 1}`),
        points: Array.isArray(points)
          ? points
            .map((point) => ({
              x: Number(point?.x ?? point?.lng),
              y: Number(point?.y ?? point?.lat),
            }))
            .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
          : [],
        bidirectional: corridor.bidirectional !== false,
      };
    })
    .filter((corridor) => corridor.points.length >= 2);
}

function normalizeGraphStats(data) {
  const graph = data?.graph || data || {};
  return {
    nodes: Number(graph.nodeCount ?? graph.nodes?.length ?? 0),
    edges: Number(graph.edgeCount ?? graph.edges?.length ?? 0),
  };
}

function unwrapRouteResponse(data) {
  return data?.data?.data || data?.data || data || {};
}

function normalizePath(data) {
  const route = unwrapRouteResponse(data);
  const rawPath =
    route.path ||
    route.routePath ||
    route.points ||
    route.result?.path ||
    route.result?.routePath ||
    route.result?.points ||
    [];

  if (!Array.isArray(rawPath)) return [];

  return rawPath
    .map((point) => ({
      x: Number(point?.x ?? point?.lng),
      y: Number(point?.y ?? point?.lat),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
}

function readPointFromParams(params, prefix) {
  const x = Number(params.get(`${prefix}X`) ?? params.get(`${prefix}x`));
  const y = Number(params.get(`${prefix}Y`) ?? params.get(`${prefix}y`));
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
}

export default function Navigation() {
  const { t } = useTranslation();
  const [searchParamsNav, setSearchParamsNav] = useSearchParams();

  const floorIdParam = searchParamsNav.get("floorId");
  const buildingId = searchParamsNav.get("buildingId");
  const startNodeId = searchParamsNav.get("startNodeId") || searchParamsNav.get("fromNodeId");
  const endNodeId = searchParamsNav.get("endNodeId") || searchParamsNav.get("toNodeId");
  const startPoint = useMemo(() => readPointFromParams(searchParamsNav, "start"), [searchParamsNav]);
  const endPoint = useMemo(() => readPointFromParams(searchParamsNav, "end"), [searchParamsNav]);

  const [floorData, setFloorData] = useState(null);
  const [serverShapes, setServerShapes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [corridors, setCorridors] = useState([]);
  const [draftPoints, setDraftPoints] = useState([]);
  const [freehandDraftPoints, setFreehandDraftPoints] = useState([]);
  const [editingCorridor, setEditingCorridor] = useState(null);
  const isFreehandDrawingRef = useRef(false);
  const [routePath, setRoutePath] = useState([]);
  const [graphStats, setGraphStats] = useState({ nodes: 0, edges: 0 });
  const [activeTool, setActiveTool] = useState("corridor");
  const [openMenu, setOpenMenu] = useState(null);

  const drawOptions = [
    { id: "corridor", name: t("admin.navigation.toolDrawPath"), icon: LineIcon },
    { id: "freehand", name: t("admin.navigation.toolFreehandPath"), icon: VectorIcon },
    { id: "preview", name: t("admin.navigation.toolPreviewPath"), icon: WalkIcon },
  ];

  const isDrawActive = ["corridor", "freehand", "preview"].includes(activeTool);
  const activeDrawOption = drawOptions.find((o) => o.id === activeTool) || drawOptions[0];
  const DropdownIcon = activeDrawOption.icon;

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const mapRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(0);

  const zoomPercent = Math.round(Math.pow(2, zoomLevel) * 100);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();

  const pickFloor = (id) => {
    setSearchParamsNav(
      { ...(buildingId ? { buildingId } : {}), floorId: String(id) },
      { replace: true }
    );
  };

  const handleCorridorClick = (path) => {
    if (activeTool === "erase") {
      if (window.confirm(t("admin.navigation.confirmDeleteCorridor", { defaultValue: "ต้องการลบทางเดินนี้ใช่หรือไม่?" }))) {
        removeCorridor(path.id);
      }
    }
  };

  function MapController() {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
      setZoomLevel(map.getZoom());
      const onZoom = () => setZoomLevel(map.getZoom());
      map.on("zoom", onZoom);
      return () => map.off("zoom", onZoom);
    }, [map]);
    return null;
  }

  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloorOptions(list);
        if (!floorIdParam && list.length > 0) {
          setSearchParamsNav(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
  }, [buildingId, floorIdParam, setSearchParamsNav]);

  useEffect(() => {
    if (!floorIdParam) {
      setFloorData(null);
      setServerShapes([]);
      setDestinations([]);
      setCorridors([]);
      setDraftPoints([]);
      setFreehandDraftPoints([]);
      setEditingCorridor(null);
      isFreehandDrawingRef.current = false;
      setRoutePath([]);
      setError("");
      return;
    }

    setServerShapes([]);
    setDestinations([]);
    NavigationAPI.getFloor(floorIdParam)
      .then((data) => setFloorData(data))
      .catch(() => setFloorData(null));

    ShapeAPI.listByFloor(floorIdParam)
      .then((data) =>
        setServerShapes(
          (Array.isArray(data) ? data : []).filter((shape) =>
            shapeBelongsToFloor(shape, floorIdParam)
          )
        )
      )
      .catch(() => setServerShapes([]));

    DestinationAPI.listByFloor(floorIdParam)
      .then((data) => setDestinations(Array.isArray(data) ? data : []))
      .catch(() => setDestinations([]));

    NavigationGraphAPI.listCorridors(floorIdParam)
      .then((data) => setCorridors(normalizeCorridors(data, t)))
      .catch((err) => {
        setCorridors([]);
        setError(err?.message || t("admin.navigation.loadPathFailed"));
      });

    NavigationGraphAPI.getGraph(floorIdParam)
      .then((data) => setGraphStats(normalizeGraphStats(data)))
      .catch(() => setGraphStats({ nodes: 0, edges: 0 }));
  }, [floorIdParam, t]);

  useEffect(() => {
    let cancelled = false;

    async function loadRoutePreview() {
      if (!floorIdParam || (!(startNodeId && endNodeId) && !(startPoint && endPoint))) {
        setRoutePath([]);
        return;
      }

      try {
        const data = await NavigationAPI.computePath({
          floorId: floorIdParam,
          startNodeId: startNodeId || undefined,
          endNodeId: endNodeId || undefined,
          start: startPoint,
          end: endPoint,
        });
        if (!cancelled) setRoutePath(normalizePath(data));
      } catch {
        if (!cancelled) setRoutePath([]);
      }
    }

    loadRoutePreview();

    return () => {
      cancelled = true;
    };
  }, [floorIdParam, startNodeId, endNodeId, startPoint, endPoint]);

  const imageUrl = resolveAsset(floorData?.imageUrl);

  const roomCount = useMemo(
    () => serverShapes.filter((shape) => !shape.hidden && shape.name).length,
    [serverShapes]
  );

  const corridorPointCount = useMemo(
    () => corridors.reduce((sum, corridor) => sum + corridor.points.length, 0),
    [corridors]
  );

  const visibleDraftPath = useMemo(() => {
    if (freehandDraftPoints.length <= 1) return draftPoints;
    const previewStroke = straightStroke(freehandDraftPoints);
    if (draftPoints.length) {
      const draftPreview = snapStrokeToCorridors([{ points: draftPoints }], previewStroke);
      return draftPreview.mode === "connected"
        ? draftPreview.points
        : mergeStraightStrokeIntoDraft(draftPoints, previewStroke).points;
    }
    const corridorPreview = snapStrokeToCorridors(corridors, previewStroke);
    return corridorPreview.mode === "ignored" ? previewStroke : corridorPreview.points;
  }, [corridors, draftPoints, freehandDraftPoints]);

  const markers = useMemo(() => {
    return destinations.map((d) => ({
      id: d.id,
      type: d.category === "stairs" ? "stair" : d.category === "elevator" ? "elevator" : d.category === "bathroom" ? "bathroom" : d.category === "counterservice" ? "counterservice" : "poi",
      x: Number(d.x) || 0,
      y: Number(d.y) || 0,
      imageUrl: d.img || d.imageUrl ? resolveAsset(d.img || d.imageUrl) : null,
      label: parseNames(d.name || d.text).primary || "",
    }));
  }, [destinations]);

  const handleMapClick = (point) => {
    if (activeTool !== "corridor") return;
    setMessage("");
    setError("");
    setDraftPoints((prev) => [...prev, point]);
  };

  const startFreehand = (point) => {
    if (activeTool !== "freehand") return;
    setMessage("");
    setError("");
    isFreehandDrawingRef.current = true;
    setFreehandDraftPoints([point]);
  };

  const moveFreehand = (point) => {
    if (activeTool !== "freehand" || !isFreehandDrawingRef.current) return;
    setFreehandDraftPoints((prev) => {
      const last = prev[prev.length - 1];
      if (last && distance(last, point) < 5) return prev;
      return [...prev, point];
    });
  };

  const endFreehand = (point) => {
    if (activeTool !== "freehand" || !isFreehandDrawingRef.current) return;
    isFreehandDrawingRef.current = false;
    setFreehandDraftPoints((prev) => {
      const raw = [...prev, point];
      const nextPoints = normalizeFreehandPath(raw);
      if (nextPoints.length < 2) {
        setError(t("admin.navigation.placeAtLeast2Points"));
        return [];
      }
      setError("");
      if (draftPoints.length) {
        const draftBranch = snapStrokeToCorridors([{ points: draftPoints }], nextPoints);
        if (draftBranch.mode === "connected" && !editingCorridor) {
          setCorridors((currentCorridors) => [
            ...currentCorridors,
            makeCorridor(draftPoints, currentCorridors.length, t),
            makeCorridor(draftBranch.points, currentCorridors.length + 1, t),
          ]);
          setDraftPoints([]);
          setMessage(t("admin.navigation.freehandPathAdded", { count: draftBranch.points.length }));
          return [];
        }

        const result = mergeStraightStrokeIntoDraft(draftPoints, nextPoints);
        if (result.mode === "ignored") {
          setMessage("");
          setError(t("admin.navigation.freehandPathConnectFromEnd"));
        } else {
          setMessage(t("admin.navigation.freehandPathAdded", { count: result.points.length }));
        }
        setDraftPoints(result.points);
        return [];
      }

      const corridorResult = snapStrokeToCorridors(corridors, nextPoints);
      if (corridorResult.mode === "ignored") {
        setMessage("");
        setError(t("admin.navigation.freehandPathConnectFromEnd"));
        return [];
      }

      if (corridorResult.mode === "new") {
        setDraftPoints(nextPoints);
        setMessage(t("admin.navigation.freehandPathAdded", { count: nextPoints.length }));
        return [];
      }

      setCorridors((currentCorridors) => [
        ...currentCorridors,
        makeCorridor(corridorResult.points, currentCorridors.length, t),
      ]);
      setMessage(t("admin.navigation.freehandPathAdded", { count: corridorResult.points.length }));
      return [];
    });
  };

  const finishCorridor = () => {
    if (draftPoints.length < 2) {
      setError(t("admin.navigation.placeAtLeast2Points"));
      return;
    }

    setCorridors((prev) => [
      ...prev,
      editingCorridor
        ? {
          ...editingCorridor,
          points: draftPoints.map((point) => ({
            x: Math.round(point.x),
            y: Math.round(point.y),
          })),
        }
        : makeCorridor(draftPoints, prev.length, t),
    ]);
    setDraftPoints([]);
    setEditingCorridor(null);
    setError("");
    setMessage(editingCorridor ? t("admin.navigation.pathUpdated") : t("admin.navigation.pathAdded"));
  };

  const undoDraftPoint = () => {
    setDraftPoints((prev) => prev.slice(0, -1));
  };

  const removeCorridor = (id) => {
    setCorridors((prev) => prev.filter((item) => item.id !== id));
  };

  const editCorridor = (corridor) => {
    if (draftPoints.length > 0 || freehandDraftPoints.length > 0) {
      setError(t("admin.navigation.finishDraftBeforeEdit"));
      return;
    }

    setEditingCorridor(corridor);
    setDraftPoints(corridor.points || []);
    setFreehandDraftPoints([]);
    setCorridors((prev) => prev.filter((item) => item.id !== corridor.id));
    setActiveTool("freehand");
    setError("");
    setMessage(t("admin.navigation.editingCorridor", { name: corridor.name || "" }));
  };

  const cancelEditCorridor = () => {
    if (!editingCorridor) {
      setDraftPoints([]);
      setFreehandDraftPoints([]);
      return;
    }

    setCorridors((prev) => [...prev, editingCorridor]);
    setEditingCorridor(null);
    setDraftPoints([]);
    setFreehandDraftPoints([]);
    setError("");
    setMessage(t("admin.navigation.editCancelled"));
  };

  const saveCorridors = async () => {
    if (!floorIdParam) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = buildCorridorPayload(corridors, t);
      const result = await NavigationGraphAPI.saveCorridors(floorIdParam, payload);
      const savedCorridors = normalizeCorridors(result, t);
      if (savedCorridors.length) setCorridors(savedCorridors);
      setMessage(t("admin.navigation.savePathSuccess"));
      return payload;
    } catch (err) {
      setError(err?.message || t("admin.navigation.savePathFailed"));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const generateGraph = async () => {
    if (!floorIdParam) return;
    setGenerating(true);
    setError("");
    setMessage("");
    try {
      await saveCorridors();
      const result = await NavigationGraphAPI.generateGraph(floorIdParam);
      const nextGraphStats = normalizeGraphStats(result);
      setGraphStats(nextGraphStats);
      const nodeCount = nextGraphStats.nodes;
      const edgeCount = nextGraphStats.edges;
      setMessage(
        t("admin.navigation.generateGraphSuccess", { nodes: nodeCount || 0, edges: edgeCount || 0 })
      );
    } catch (err) {
      setError(err?.message || t("admin.navigation.generateGraphFailed"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="font-sans w-full h-full overflow-hidden">
      <div className="flex w-full h-full overflow-hidden">
        <div className="w-[70%] min-w-0 flex flex-col">
          {/* Top bar: floor selector and tools */}
          <div className="flex items-center justify-between bg-white px-3 shrink-0 border-b border-gray-200" style={{ height: '72px' }}>
            {/* floor tabs */}
            <div className="flex items-center gap-1 overflow-x-auto mr-8">
              {floorOptions.map((f) => (
                <button
                  key={f.id}
                  onClick={() => pickFloor(f.id)}
                  className={`px-5 h-10 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${String(f.id) === String(floorIdParam)
                    ? "bg-blue-500 text-white"
                    : "bg-white text-slate-500 hover:bg-gray-100"
                    }`}
                >
                  {getFloorLabel(f, t)}
                </button>
              ))}
              {floorOptions.length === 0 && (
                <span className="text-xs text-slate-400">
                  {t("admin.navigation.noFloorsWarning", "ยังไม่มีชั้น — กรุณาเพิ่มชั้นในหน้าตั้งค่าอาคารก่อน")}
                </span>
              )}
            </div>

            {/* tools — right */}
            <div className="flex items-center gap-0.5">
              {/* mouse */}
              <button
                title={t("admin.navigation.toolSelectEdit", { defaultValue: "เลือก/แก้ไข" })}
                onClick={() => {
                  setActiveTool("mouse");
                  setOpenMenu(null);
                }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "mouse" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <ClickIcon className="w-4 h-4" />
              </button>

              {/* hand */}
              <button
                title={t("admin.navigation.toolPanDrag", { defaultValue: "เลื่อน/ลาก" })}
                onClick={() => {
                  setActiveTool("hand");
                  setOpenMenu(null);
                }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "hand" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <HandIcon className="w-4 h-4" />
              </button>

              {/* eye */}
              <button
                title={t("admin.navigation.toolView", { defaultValue: "ดูแผนที่" })}
                onClick={() => {
                  setActiveTool("eye");
                  setOpenMenu(null);
                }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "eye" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <EyeIcon className="w-4 h-4" />
              </button>

              {/* Draw dropdown */}
              <div className="relative">
                <button
                  title={isDrawActive ? activeDrawOption.name : t("admin.navigation.toolDrawPath")}
                  onClick={() => {
                    if (!isDrawActive) {
                      setActiveTool(activeDrawOption.id);
                    }
                    setOpenMenu(openMenu === "draw" ? null : "draw");
                  }}
                  className={`w-14 h-10 flex items-center justify-center gap-1 rounded-lg transition-colors ${isDrawActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-600 hover:bg-gray-100"
                    }`}
                >
                  <DropdownIcon className="w-4 h-4" />
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {openMenu === "draw" && (
                  <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-slate-200 bg-white shadow-xl z-[1000]">
                    {drawOptions.map((opt) => {
                      const OptIcon = opt.icon;
                      return (
                        <button
                          type="button"
                          key={opt.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(opt.id);
                            setOpenMenu(null);
                          }}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left ${activeTool === opt.id ? "bg-blue-50 text-blue-600" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                          <OptIcon className="w-4 h-4 shrink-0" />
                          <span className="font-semibold">{opt.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* erase */}
              <button
                title={t("admin.navigation.toolErase", { defaultValue: "ลบเส้นทาง" })}
                onClick={() => {
                  setActiveTool("erase");
                  setOpenMenu(null);
                }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "erase" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <EraseIcon className="w-4 h-4" />
              </button>

              {/* zoom + */}
              <button
                onClick={handleZoomIn}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>

              {/* zoom % */}
              <span className="text-sm text-slate-500 select-none min-w-[44px] text-center">
                {zoomPercent}%
              </span>

              {/* zoom − */}
              <button
                onClick={handleZoomOut}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14" />
                </svg>
              </button>
            </div>
          </div>

          <div className="relative flex-1 min-h-[500px] overflow-hidden bg-slate-200">
            {imageUrl ? (
              <div className="relative w-full h-full">
                <LeafletFloorMap
                  imageUrl={imageUrl}
                  imageWidth={floorData?.imageWidth}
                  imageHeight={floorData?.imageHeight}
                  shapes={serverShapes}
                  markers={markers}
                  corridorPaths={corridors}
                  onCorridorClick={handleCorridorClick}
                  routePath={activeTool === "preview" ? routePath : null}
                  onMapClick={handleMapClick}
                  freehandEnabled={activeTool === "freehand"}
                  onFreehandStart={startFreehand}
                  onFreehandMove={moveFreehand}
                  onFreehandEnd={endFreehand}
                  draftPath={visibleDraftPath}
                  className="absolute inset-0 w-full h-full"
                >
                  <MapController />
                </LeafletFloorMap>

                {(draftPoints.length > 0 || freehandDraftPoints.length > 0) && (
                  <div className="absolute left-3 top-3 z-30 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-bold text-orange-700 shadow">
                    {t("admin.navigation.drawingPointsCount", {
                      count: freehandDraftPoints.length || draftPoints.length,
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                {floorIdParam ? t("admin.navigation.noFloorMap") : t("admin.navigation.selectFloorPrompt")}
              </div>
            )}
          </div>
        </div>

        <div className="w-[30%] min-w-[340px] h-full flex flex-col border-l border-gray-200 bg-white">
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="font-black text-slate-900 border-b-2 border-slate-200 pb-3">
              {t("admin.navigation.corridorsNetwork")}
              <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                {t("admin.navigation.corridorsSubText")}
              </p>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <InfoBox label={t("admin.navigation.roomsCount")} value={roomCount} />
              <InfoBox label={t("admin.navigation.routesCount")} value={corridors.length} />
              <InfoBox label={t("admin.navigation.pointsCount")} value={corridorPointCount} />
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <InfoBox label={t("admin.navigation.graphNodes")} value={graphStats.nodes} />
              <InfoBox label={t("admin.navigation.graphEdges")} value={graphStats.edges} />
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-black text-slate-800">
                {editingCorridor ? t("admin.navigation.editingLine") : t("admin.navigation.drawingLine")}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={finishCorridor}
                  disabled={draftPoints.length < 2}
                  className="flex-1 rounded-lg bg-orange-500 px-3 py-2 text-sm font-black text-white disabled:opacity-50"
                >
                  {t("admin.navigation.finishLine")}
                </button>
                <button
                  onClick={undoDraftPoint}
                  disabled={!draftPoints.length}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-600 disabled:opacity-50"
                >
                  {t("admin.navigation.undo")}
                </button>
                <button
                  onClick={editingCorridor ? cancelEditCorridor : () => setDraftPoints([])}
                  disabled={!draftPoints.length}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-600 disabled:opacity-50"
                >
                  {editingCorridor ? t("common.cancel") : t("admin.navigation.clear")}
                </button>
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-sm font-black text-slate-800">{t("admin.navigation.corridorsList")}</div>
              <div className="max-h-[330px] overflow-y-auto rounded-xl border border-slate-200">
                {corridors.length === 0 ? (
                  <div className="p-4 text-sm font-semibold text-slate-400">
                    {t("admin.navigation.noCorridorsPrompt")}
                  </div>
                ) : (
                  corridors.map((corridor, index) => (
                    <div
                      key={corridor.id}
                      className="flex items-center justify-between gap-2 border-b border-slate-100 p-3 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-black text-slate-800">
                          {corridor.name || t("admin.navigation.corridorName", { index: index + 1 })}
                        </div>
                        <div className="text-xs font-semibold text-slate-400">
                          {t("admin.navigation.pointsCountLabel", { count: corridor.points.length })}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          onClick={() => editCorridor(corridor)}
                          className="rounded-lg border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-black text-blue-600"
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          onClick={() => removeCorridor(corridor.id)}
                          className="rounded-lg border border-red-100 bg-red-50 px-2 py-1 text-xs font-black text-red-600"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {message && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-bold text-green-700">
                {message}
              </div>
            )}
            {error && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-600">
                {error}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 p-3">
            <div className="flex gap-2">
              <button
                onClick={saveCorridors}
                disabled={saving || !floorIdParam}
                className="flex-1 rounded-lg border border-blue-200 bg-blue-50 py-2 font-black text-blue-600 disabled:opacity-50"
              >
                {saving ? t("common.saving") : t("admin.navigation.saveCorridors")}
              </button>
              <button
                onClick={generateGraph}
                disabled={generating || !floorIdParam}
                className="flex-1 rounded-lg bg-blue-600 py-2 font-black text-white disabled:opacity-50"
              >
                {generating ? t("common.generating") : t("admin.navigation.generateGraph")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-center">
      <div className="text-2xl font-black text-slate-900">{value}</div>
      <div className="text-xs font-bold text-slate-400">{label}</div>
    </div>
  );
}
